/**
 * Validation Utilities
 * 
 * This file contains regex patterns and validation functions for common data types
 * like PAN, Email, Phone Number, and GST Number that can be used across the application.
 */

/**
 * Regex patterns for validation
 */
export const VALIDATION_PATTERNS = {
  /**
   * PAN (Permanent Account Number) - Indian tax identification number
   * Format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)
   */
  PAN: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,

  /**
   * Email address validation
   * Standard email format validation
   */
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,

  /**
   * Phone Number - Indian phone numbers
   * Supports formats: +91XXXXXXXXXX, 91XXXXXXXXXX, 0XXXXXXXXXX, XXXXXXXXXX
   * Validates 10-digit mobile numbers
   */
  PHONE_NUMBER: /^(\+91|91|0)?[6-9]\d{9}$/,

  /**
   * GST Number (GSTIN) - Indian Goods and Services Tax Identification Number
   * Format: 15 characters - 2 state code, 10 PAN, 1 entity number, 1 Z, 1 check digit
   * Example: 27ABCDE1234F1Z5
   */
  GST_NUMBER: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,

  /**
   * IFSC code - Indian Financial System Code
   * Format: 11 characters - 4 letters, 0, 6 alphanumeric (e.g., SBIN0001234)
   */
  IFSC: /^[A-Z]{4}0[A-Z0-9]{6}$/,

  /**
   * Bank Account Number
   * Format: 9-18 alphanumeric characters (no special characters)
   */
  BANK_ACCOUNT_NUMBER: /^[A-Za-z0-9]{9,18}$/,

  /**
   * Aadhar Number - Indian unique identification number
   * Format: 12 digits (e.g., 123456789012)
   */
  AADHAR: /^[0-9]{12}$/,

  /**
   * TAN (Tax Deduction and Collection Account Number) - Indian tax identification number
   * Format: 4 letters, 5 digits, 1 letter (e.g., ABCD12345E)
   */
  TAN: /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/
};

/**
 * Validation functions
 */
export class ValidationUtils {
  /**
   * Validates PAN number
   * @param pan - PAN number to validate
   * @returns true if valid, false otherwise
   */
  static isValidPAN(pan: string): boolean {
    if (!pan || typeof pan !== 'string') {
      return false;
    }
    return VALIDATION_PATTERNS.PAN.test(pan.toUpperCase().trim());
  }

  /**
   * Validates IFSC code
   * @param ifsc - IFSC code to validate
   * @returns true if valid, false otherwise
   */
  static isValidIFSC(ifsc: string): boolean {
    if (!ifsc || typeof ifsc !== 'string') {
      return false;
    }
    return VALIDATION_PATTERNS.IFSC.test(ifsc.toUpperCase().trim());
  }

  /**
   * Validates email address
   * @param email - Email address to validate
   * @returns true if valid, false otherwise
   */
  static isValidEmail(email: string): boolean {
    if (!email || typeof email !== 'string') {
      return false;
    }
    return VALIDATION_PATTERNS.EMAIL.test(email.trim());
  }

  /**
   * Validates phone number (Indian format)
   * @param phone - Phone number to validate
   * @returns true if valid, false otherwise
   */
  static isValidPhoneNumber(phone: string): boolean {
    if (!phone || typeof phone !== 'string') {
      return false;
    }
    // Remove spaces, dashes, and other common separators
    const cleanedPhone = phone.replace(/[\s\-\(\)]/g, '');
    return VALIDATION_PATTERNS.PHONE_NUMBER.test(cleanedPhone);
  }

  /**
   * Validates GST number
   * @param gst - GST number to validate
   * @returns true if valid, false otherwise
   */
  static isValidGSTNumber(gst: string): boolean {
    if (!gst || typeof gst !== 'string') {
      return false;
    }
    return VALIDATION_PATTERNS.GST_NUMBER.test(gst.toUpperCase().trim());
  }

  /**
   * Validates Aadhar number
   * @param aadhar - Aadhar number to validate
   * @returns true if valid, false otherwise
   */
  static isValidAadhar(aadhar: string): boolean {
    if (!aadhar || typeof aadhar !== 'string') {
      return false;
    }
    // Remove spaces and dashes if present
    const cleanedAadhar = aadhar.replace(/[\s\-]/g, '');
    return VALIDATION_PATTERNS.AADHAR.test(cleanedAadhar);
  }

  /**
   * Validates TAN number
   * @param tan - TAN number to validate
   * @returns true if valid, false otherwise
   */
  static isValidTAN(tan: string): boolean {
    if (!tan || typeof tan !== 'string') {
      return false;
    }
    return VALIDATION_PATTERNS.TAN.test(tan.toUpperCase().trim());
  }

  /**
   * Validates bank account number
   * @param accountNumber - Bank account number to validate
   * @returns true if valid, false otherwise
   */
  static isValidBankAccountNumber(accountNumber: string): boolean {
    if (!accountNumber || typeof accountNumber !== 'string') {
      return false;
    }
    return VALIDATION_PATTERNS.BANK_ACCOUNT_NUMBER.test(accountNumber.trim());
  }

  /**
   * Parent validation function that routes to the appropriate validator based on type
   * @param value - The value to validate
   * @param type - The type of validation to perform ('pan', 'email', 'phone', 'phone_number', 'gst', 'gst_number')
   * @returns true if valid, false otherwise
   */
  static validate(value: string, type: ValidationType): boolean {
    if (!value || typeof value !== 'string') {
      return false;
    }

    switch (type.toLowerCase()) {
      case 'pan':
        return this.isValidPAN(value);
      
      case 'email':
        return this.isValidEmail(value);
      
      case 'phone':
      case 'phone_number':
        return this.isValidPhoneNumber(value);
      
      case 'gst':
      case 'gst_number':
        return this.isValidGSTNumber(value);
      
      case 'aadhar':
      case 'aadhaar':
        return this.isValidAadhar(value);
      
      case 'tan':
        return this.isValidTAN(value);
      
      case 'ifsc':
        return this.isValidIFSC(value);
      
      case 'bank_account_number':
        return this.isValidBankAccountNumber(value);
      
      default:
        console.warn(`Unknown validation type: ${type}`);
        return false;
    }
  }
}

/**
 * Convenience functions for direct validation
 */
export const isValidPAN = (pan: string): boolean => ValidationUtils.isValidPAN(pan);
export const isValidEmail = (email: string): boolean => ValidationUtils.isValidEmail(email);
export const isValidPhoneNumber = (phone: string): boolean => ValidationUtils.isValidPhoneNumber(phone);
export const isValidGSTNumber = (gst: string): boolean => ValidationUtils.isValidGSTNumber(gst);
export const isValidAadhar = (aadhar: string): boolean => ValidationUtils.isValidAadhar(aadhar);
export const isValidTAN = (tan: string): boolean => ValidationUtils.isValidTAN(tan);

/**
 * Validation type options
 */
export type ValidationType = 'pan' | 'email' | 'phone' | 'phone_number' | 'gst' | 'gst_number' | 'aadhar' | 'aadhaar' | 'tan' | 'ifsc' | 'bank_account_number';

/**
 * Parent validation function that routes to the appropriate validator based on type
 * @param value - The value to validate
 * @param type - The type of validation to perform ('pan', 'email', 'phone', 'phone_number', 'gst', 'gst_number', 'aadhar', 'aadhaar', 'tan')
 * @returns true if valid, false otherwise
 */
export function validate(value: string, type: ValidationType): boolean {
  return ValidationUtils.validate(value, type);
}

